import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment'
import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { map, mergeMap } from 'rxjs/operators';
import { Observable, throwError, forkJoin, of, Subject } from 'rxjs';
import { AuthService, FileService, CacheService } from '@mapfre-tech/amap3';
import moment from 'moment';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { AppUser, normalizeUsers } from './user.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  className = 'UserService';

  constructor(private http: HttpClient, private authService: AuthService, private fileService: FileService,
    private cacheService: CacheService, private router: Router) { }

  getAllUsers(cache = true, cacheKey = "allUsers"): Observable<AppUser[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      const observable = this.http.get<any[]>(environment.base_URL + `/users`).pipe(map((raw) => {
        const res = normalizeUsers(raw);
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getUserByRoleForkJoin(role, os = null, cache?, cacheKey = "userRoles") {
    const result = forkJoin(_.map(role, rol => this.getUserByRole(rol, os, cache, cacheKey = "userRoles")));
    return result.pipe(map((res) => { return _.uniqBy(_.flatten(res), 'idUserPk') }));
  }

  getUserByRole(role, os: any = null, cache = true, cacheKey = "userRoles"): Observable<AppUser[]> {

    const params: any = {};

    if (role) {

      if (!_.isArray(role)) {
        role = [role];
      }

      params.txtTypeRole = role.join(',');
    }
    else {
      role = [];
    }

    if (os) {
      if (!_.isArray(os)) {
        os = [os];
      }
      params.orgStrIds = os.join(',');
    }
    else {
      os = []
    }

    if (this.cacheService.canUseCache(cacheKey, [role, os]) && cache) {
      return this.cacheService.getCacheData(cacheKey, [role.join(','), os]);
    } else {
      const observable = this.http.get<any[]>(environment.base_URL + `/users/roles`, { params }).pipe(map((raw) => {
        const res = normalizeUsers(raw);
        this.cacheService.updateCacheData(res, cacheKey, [role.join(','), os])
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey, [role.join(','), os]);
      return observable;
    }
  }

  getUserProfile(cache = true, cacheKey = "userProfile"): Observable<AppUser> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      const observable = this.http.get<AppUser>(environment.base_URL + `/users/profile`).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }

  }

  getUserProfileById(userId): Observable<AppUser> {
    const params: any = {};
    params.onlySign = true;
    if (userId) {
      params.userId = userId;
    }
    return this.http.get<AppUser>(environment.base_URL + `/users/profile`, { params });
  }

  getUserProfileNoCache(userId = null): Observable<AppUser> {
    const params: any = {};
    if (userId) {
      params.userId = userId;
    }
    return this.http.get<AppUser>(environment.base_URL + `/users/profile`, { params });
  }


  getUserEvaluationProfile(userId: any = null, questionnaireTypeIds: any = null, exercises: any = null, evaluator = null): any {
    const params: any = {};

    if (questionnaireTypeIds) {
      params.questionnaireTypeIds = questionnaireTypeIds.join(',');
    }
    if (exercises) {
      params.exercises = exercises.join(',');
    }
    if (evaluator != null) {
      params.evaluator = evaluator;
    }

    if (userId) {
      params.userId = userId;
    }

    return this.http.get(environment.base_URL + "/users/evaluations", { params }).pipe(map(res => {
      return _.map(res, (q: any) => {
        let totalValoration = 0;
        let totalNumerableQuestions = 0;
        _.forEach(q.answers, (e) => {
          const question = _.get(e, 'question');
          const isNumerable = _.get(question, 'optionQuestion');
          const valoration = _.get(e, 'mumEvaluationValue', 0);
          if (isNumerable) {
            totalNumerableQuestions++;
            totalValoration += valoration;
          }
        })
        q.answers = _.sortBy(q.answers, (e) => {
          return _.get(e, 'nmrOrder');
        });
        q.questionnaire.valoration = totalNumerableQuestions ? _.round((totalValoration / totalNumerableQuestions), 2) : 0;
        return q;
      })
    }));
  }

  getUserBycdNuuma(nuuma, cache = true, cacheKey = "userByNuuma"): Observable<AppUser> {
    cacheKey = cacheKey+"_"+nuuma.toString()
    
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      const observable = this.http.get<AppUser>(environment.base_URL + `/users/${nuuma}`).pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  newUser(newUser: any) {
    return this.http.post(environment.base_URL + "/users", newUser, { observe: 'response' });
  }

  newUserProfile(newUser: any) {
    return this.http.post(environment.base_URL + "/users/profile", newUser);
  }

  updateUser(editedUser, idUserPk) {
    return this.http.put(environment.base_URL + `/users/${idUserPk}`, editedUser)
  }

  newUserWithFiles(newUser: any) {
    const filesToUpload = {}
    if (_.get(newUser, 'filePhotoName.type')) {
      const file = _.get(newUser, 'filePhotoName');
      filesToUpload['user-photo'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'filePhotoName', filename);
    }
    if (_.get(newUser, 'fileFirmName.type')) {
      const file = _.get(newUser, 'fileFirmName');
      filesToUpload['user-sign'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'fileFirmName', filename);
    }
    if (_.get(newUser, 'fileCvEsName.type')) {
      const file = _.get(newUser, 'fileCvEsName');
      filesToUpload['user-cv-es'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'fileCvEsName', filename);
    }
    if (_.get(newUser, 'fileCvEnName.type')) {
      const file = _.get(newUser, 'fileCvEnName');
      filesToUpload['user-cv-en'] = file;
      const filename = _.get(file, 'name');
      newUser = _.set(newUser, 'fileCvEnName', filename);
    }

    if (!_.isEmpty(_.values(filesToUpload))) {
      return this.newUser(newUser).pipe(mergeMap((res: any) => {
        return this.fileService.upload(filesToUpload['user-photo'], 'user-photo', res).pipe(mergeMap((res1: any) => {
          return this.fileService.upload(filesToUpload['user-sign'], 'user-sign', res).pipe(mergeMap((res2: any) => {
            return this.fileService.upload(filesToUpload['user-cv-es'], 'user-cv-es', res).pipe(mergeMap((res3: any) => {
              return this.fileService.upload(filesToUpload['user-cv-en'], 'user-cv-en', res).pipe(map((res4: any) => {
                return res;
              }));
            }));
          }));
        }))
        /*let observables = _.map(filesToUpload, (value, key) => this.fileService.upload(value, key, res));
return forkJoin(observables).pipe(map(resultFiles => {
  return res;
}));*/
      }))
    }
    else {
      return this.newUser(newUser);
    }
  }

  updateUserWithFiles(editedUser, idUserPk) {
    const filesToUpload = {}
    if (_.get(editedUser, 'filePhotoName.type')) {
      const file = _.get(editedUser, 'filePhotoName');
      filesToUpload['user-photo'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'filePhotoName', filename);
    }
    if (_.get(editedUser, 'fileFirmName.type')) {
      const file = _.get(editedUser, 'fileFirmName');
      filesToUpload['user-sign'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'fileFirmName', filename);
    }
    if (_.get(editedUser, 'fileCvEsName.type')) {
      const file = _.get(editedUser, 'fileCvEsName');
      filesToUpload['user-cv-es'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'fileCvEsName', filename);
    }
    if (_.get(editedUser, 'fileCvEnName.type')) {
      const file = _.get(editedUser, 'fileCvEnName');
      filesToUpload['user-cv-en'] = file;
      const filename = _.get(file, 'name');
      editedUser = _.set(editedUser, 'fileCvEnName', filename);
    }

    if (!_.isEmpty(_.values(filesToUpload))) {
      return this.updateUser(editedUser, idUserPk).pipe(mergeMap((res: any) => {
        return this.fileService.upload(filesToUpload['user-photo'], 'user-photo', res).pipe(mergeMap((res1: any) => {
          return this.fileService.upload(filesToUpload['user-sign'], 'user-sign', res).pipe(mergeMap((res2: any) => {
            return this.fileService.upload(filesToUpload['user-cv-es'], 'user-cv-es', res).pipe(mergeMap((res3: any) => {
              return this.fileService.upload(filesToUpload['user-cv-en'], 'user-cv-en', res).pipe(map((res4: any) => {
                return res;
              }));
            }));
          }));
        }))
        /*let observables = _.map(filesToUpload, (value, key) => this.fileService.upload(value, key, idUserPk));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));*/
      }))
    }
    else {
      return this.updateUser(editedUser, idUserPk);
    }
  }


  deleteUser(id: number) {
    return this.http.delete(environment.base_URL + `/user/${id}`)
  }

  /* Cambiar a ID */
  getUserByID(userId: any = null, userNuuma: any = null, cache = true, cacheKey = "userById"): any {

    const params: any = {};

    if (userId) {
      cacheKey = cacheKey+"_"+userId.toString()
      params.userId = userId;
    }

    if (userNuuma) {
      cacheKey = cacheKey+"_"+userNuuma.toString()
      params.userNuuma = userNuuma;
    }

    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      const observable = this.http.get<any>(environment.base_URL + `/users`, { params }).pipe(map((raw) => {
        const res = normalizeUsers(raw);
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  newGetUserByRoles(role): Observable<AppUser[]> {
    return this.getAllUsers().pipe(map<AppUser[], AppUser[]>(res => _.filter(res, ['typeRole.txtTypeRole', role])));
  }

  getUsersByOS(id: any, cache?, cacheKey = "getUsersByOS") {
    if (this.cacheService.canUseCache(cacheKey, [id]) && cache) {
      return this.cacheService.getCacheData(cacheKey, [id]);
    } else {
      const observable = this.http.get(environment.base_URL + "/users/os/" + id.join(',')).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey, [id])
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey, [id]);
      return observable;
    }

  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(
      'Something bad happened; please try again later.');
  };


  uploadFile(idUserPk, file: FormData) {
    const token = `Bearer ${this.authService.getToken()}`;

    return this.http.post(`http://ec2-34-247-190-210.eu-west-1.compute.amazonaws.com:8080/amap3_be_web/api` + `/users/${idUserPk}/file`,
      file
      , {
        headers: new HttpHeaders().set('authorization', token),
        reportProgress: true
      });
  }

  downloadFile(idUserPk, userField: string) {

    const token = `Bearer ${this.authService.getToken()}`;

    return this.http.get(`http://ec2-34-247-190-210.eu-west-1.compute.amazonaws.com:8080/amap3_be_web/api` + `/users/${idUserPk}/file`,
      {
        params: { userField: userField },
        headers: new HttpHeaders().set('authorization', token).set("Accept", "application/octet-stream"),

        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  uploadUserFiles(idUserPk, files: FormData[]) {
    const result = forkJoin(
      _.map(files, (item) => {
        return this.uploadFile(idUserPk, item);
      })
    );
    return result;
  }

  deleteUserFile(idUserPk, userField: string) {

    const token = `Bearer ${this.authService.getToken()}`;

    return this.http.delete(`http://ec2-34-247-190-210.eu-west-1.compute.amazonaws.com:8080/amap3_be_web/api` + `/users/${idUserPk}/file`,
      {
        params: { userField: userField },
        headers: new HttpHeaders().set('authorization', token),
        observe: 'response'
      }
    );
  }

  //!NO se usa
  getUsersByEntitiesIds(ids) {
    const options = {
      params: {
        entitiesIds: ids
      }
    }
    return this.http.get<any[]>(environment.base_URL + "/users?entitiesIds=" + _.join(ids, ',')).pipe(map(res => {
      return normalizeUsers(res);
    }))
  }

  getUsersByCategoryAndOS(categories, os) {
    const params = {
      categoryIds: categories.join(','),
      orgStrIds: os.join(',')
    }
    return this.http.get<any[]>(environment.base_URL + "/users", { params }).pipe(map(res => {
      return normalizeUsers(res);
    }))
  }

  deleteCache() {

    const memoryCache = ["profile", "getUsersByOS", "userRoles", "allUsers"];

    _.forEach(memoryCache, item => {
      this.cacheService.deleteCache(item);
    })

  }


}
